import ClockoutController from '../../core/clockout.controller';
import Inherit from '../../core/inherit';
import ConfigurationDelegate from './configuration.delegate';

/**
 * @extends ClockoutController
 * @extends ConfigurationDelegate
 */
class ConfigurationController extends Inherit(ClockoutController, ConfigurationDelegate) {
    async initialize() {
        await this.loadConfiguration();
    }

    async loadConfiguration() {
        try {
            this.setLoading(true);
            const fiData = await this.data.fetchFI();

            let originalSegmentPolicy = null;
            let originalBalanceRules = null;
            let originalFeePolicy = null;
            let originalFeeRules = null;
            let originalLimitPolicy = null;
            let originalLimitsEnabled = null;
            let originalFeeEnabled = null;

            if (fiData?.segment_policy) {
                // Transform snake_case from backend to camelCase for frontend
                const transformed = this.transformSegmentPolicyToCamelCase(fiData.segment_policy);
                originalSegmentPolicy = JSON.parse(JSON.stringify(transformed));
            }
            let originalBalanceEnabled = null;
            if (fiData?.balance_rules) {
                const transformed = this.transformBalanceRulesToCamelCase(fiData.balance_rules);
                const { balanceEnabled, ...balanceRulesOnly } = transformed;
                originalBalanceRules = JSON.parse(JSON.stringify(balanceRulesOnly));
                originalBalanceEnabled = { ...balanceEnabled };
            }
            if (fiData?.fee_policy) {
                const transformed = this.transformFeePolicyToCamelCase(fiData.fee_policy);
                originalFeePolicy = JSON.parse(JSON.stringify(transformed));
            }
            if (fiData?.fee_rules) {
                const transformed = this.transformFeeRulesToCamelCase(fiData.fee_rules);
                originalFeeRules = JSON.parse(JSON.stringify(transformed));
            }
            if (fiData?.fee_enabled) {
                originalFeeEnabled = JSON.parse(JSON.stringify(fiData.fee_enabled));
            }
            if (!originalFeeEnabled) {
                originalFeeEnabled = JSON.parse(JSON.stringify(this.state.fee_enabled));
            }
            if (fiData?.limit_policy) {
                const transformed = this.transformLimitPolicyToCamelCase(fiData.limit_policy);
                originalLimitPolicy = JSON.parse(JSON.stringify(transformed));
            }
            if (fiData?.limits_enabled) {
                originalLimitsEnabled = JSON.parse(JSON.stringify(fiData.limits_enabled));
            }
            if (!originalLimitsEnabled) {
                originalLimitsEnabled = JSON.parse(JSON.stringify(this.state.limits_enabled));
            }

            const initialThreshold = fiData?.threshold !== undefined ? fiData.threshold : this.state.threshold;
            const initialDoubleDip = fiData?.double_dip_cap !== undefined ? fiData.double_dip_cap : this.state.doubleDipCap;
            const initialBalanceRules = originalBalanceRules || JSON.parse(JSON.stringify(this.state.balance_rules));
            const initialBalanceEnabled = originalBalanceEnabled || JSON.parse(JSON.stringify(this.state.balance_enabled));

            const originalState = {
                segment_policy: originalSegmentPolicy,
                threshold: initialThreshold,
                doubleDipCap: initialDoubleDip,
                balance_rules: initialBalanceRules,
                balance_enabled: initialBalanceEnabled,
                fee: fiData?.fee !== undefined ? fiData.fee : null,
                fee_policy: originalFeePolicy,
                fee_rules: originalFeeRules,
                fee_enabled: originalFeeEnabled,
                limit_policy: originalLimitPolicy,
                limits_enabled: originalLimitsEnabled
            };

            let transformedSegmentPolicy = null;
            // Load all policies from service (transform snake_case to camelCase)
            if (fiData?.segment_policy) {
                transformedSegmentPolicy = this.transformSegmentPolicyToCamelCase(fiData.segment_policy);
                this.setSegmentPolicy(transformedSegmentPolicy);
            }

            if (fiData?.threshold !== undefined) {
                // Service returns decimal (e.g., 0.7), convert to percentage for setThreshold (0.7 -> 70)
                // setThreshold will convert it back to decimal for storage
                this.setThreshold(fiData.threshold * 100);
            }
            if (fiData?.double_dip_cap !== undefined) {
                this.setDoubleDipCap(fiData.double_dip_cap * 100);
            }

            if (fiData?.balance_rules) {
                const transformedBalanceRules = this.transformBalanceRulesToCamelCase(fiData.balance_rules);
                const { balanceEnabled, ...balanceRulesOnly } = transformedBalanceRules;
                this.setBalanceRules(balanceRulesOnly, balanceEnabled, transformedSegmentPolicy ?? this.state.segment_policy, this.state.segmentation_enabled);
            }

            if (fiData?.fee !== undefined) {
                // Ensure fee is converted to number
                const numericFee = typeof fiData.fee === 'string' ? parseFloat(fiData.fee) : fiData.fee;
                this.setFee(isNaN(numericFee) ? 0 : numericFee);
            }

            if (fiData?.fee_policy) {
                const transformedFeePolicy = this.transformFeePolicyToCamelCase(fiData.fee_policy);
                this.setFeePolicy(transformedFeePolicy);
            }

            if (fiData?.fee_rules) {
                const transformedFeeRules = this.transformFeeRulesToCamelCase(fiData.fee_rules);
                this.setFeeRules(transformedFeeRules);
            }

            if (fiData?.fee_enabled) {
                this.setFeeEnabled(fiData.fee_enabled);
            }

            if (fiData?.limit_policy) {
                const transformedLimitPolicy = this.transformLimitPolicyToCamelCase(fiData.limit_policy);
                this.setLimitPolicy(transformedLimitPolicy);
            }

            if (fiData?.limits_enabled) {
                this.setLimitsEnabled(fiData.limits_enabled);
            }

            const segmentPolicy = fiData?.segment_policy;

            const hasSegmentPolicy =
                segmentPolicy &&
                Object.keys(segmentPolicy).length > 0 &&
                Object.values(segmentPolicy).some((value) => {
                    if (Array.isArray(value)) {
                        return value.length > 0;
                    }
                    return value !== null && value !== undefined && value !== '';
                });

            if (hasSegmentPolicy) {
                this.setState({ firstTimeSetup: false, originalState, user: this.data.user });
            } else {
                this.setState({ firstTimeSetup: true, originalState, user: this.data.user });
            }
        } catch (error) {
            this.setError(true);
        } finally {
            this.setLoading(false);
        }
    }

    // Step navigation
    goToNextStep() {
        const { currentStep } = this.state;
        if (currentStep < 4) {
            this.setCurrentStep(currentStep + 1);
        } else {
            // Navigate to dashboard after success
            this.props.navigate('/dashboard');
        }
    }

    goToPreviousStep() {
        const { currentStep } = this.state;
        if (currentStep > 0) {
            this.setCurrentStep(currentStep - 1);
        }
    }

    handleContinue = () => {
        this.goToNextStep();
    };

    handleCancel = () => {
        // Navigate back or show confirmation
        this.props.navigate('/dashboard');
    };

    handleStepChange = (step) => {
        this.setCurrentStep(step);
    };

    // Change detection methods
    hasSegmentChanges() {
        const { segment_policy, originalState } = this.state;
        if (!originalState.segment_policy || !segment_policy) return false;
        return JSON.stringify(segment_policy) !== JSON.stringify(originalState.segment_policy);
    }

    hasBalanceChanges() {
        const { threshold, doubleDipCap, balance_rules, balance_enabled, originalState } = this.state;
        const thresholdChanged = originalState.threshold !== undefined && threshold !== originalState.threshold;
        const doubleDipChanged = originalState.doubleDipCap !== undefined && doubleDipCap !== originalState.doubleDipCap;
        const rulesChanged = originalState.balance_rules ? JSON.stringify(balance_rules) !== JSON.stringify(originalState.balance_rules) : false;
        const enabledChanged = originalState.balance_enabled ? JSON.stringify(balance_enabled) !== JSON.stringify(originalState.balance_enabled) : false;
        return thresholdChanged || doubleDipChanged || rulesChanged || enabledChanged;
    }

    hasFeeChanges() {
        const { fee, fee_policy, fee_rules, fee_enabled, originalState } = this.state;
        const feeChanged = originalState.fee !== undefined && fee !== originalState.fee;
        const policyChanged = originalState.fee_policy ? JSON.stringify(fee_policy) !== JSON.stringify(originalState.fee_policy) : false;
        const rulesChanged = originalState.fee_rules ? JSON.stringify(fee_rules) !== JSON.stringify(originalState.fee_rules) : false;
        const enabledChanged = originalState.fee_enabled ? JSON.stringify(fee_enabled) !== JSON.stringify(originalState.fee_enabled) : false;
        return feeChanged || policyChanged || rulesChanged || enabledChanged;
    }

    hasLimitChanges() {
        const { limit_policy, limits_enabled, originalState } = this.state;
        if (!originalState.limit_policy || !limit_policy) return false;
        const policyChanged = JSON.stringify(limit_policy) !== JSON.stringify(originalState.limit_policy);
        const enabledChanged = originalState.limits_enabled ? JSON.stringify(limits_enabled) !== JSON.stringify(originalState.limits_enabled) : false;
        return policyChanged || enabledChanged;
    }

    // Submit handlers for each component
    async handleSubmitSegmentation() {
        try {
            this.setLoading(true);
            const { segment_policy } = this.state;
            const aba = this.user?.fi?.aba || this.user?.access?.[0];

            if (!aba) {
                this.showNotification('error', 'Error', 'Unable to find FI ABA');
                return;
            }

            // Transform to snake_case for API (only includes fields from the form)
            const transformedSegmentPolicy = this.transformSegmentPolicyToSnakeCase(segment_policy);

            const response = await this.data.updateFI(aba, {
                fi_info: { segment_policy: transformedSegmentPolicy }
            });

            if (response) {
                // Update original state to reflect saved changes (transform back to camelCase for comparison)
                const originalSegmentPolicy = this.transformSegmentPolicyToCamelCase(transformedSegmentPolicy);
                this.setState((prevState) => ({
                    originalState: {
                        ...prevState.originalState,
                        segment_policy: JSON.parse(JSON.stringify(originalSegmentPolicy))
                    }
                }));
                this.showNotification('success', 'Success', 'Segmentation configuration updated successfully');
            }
        } catch (error) {
            console.error('Error updating segmentation:', error);
            this.showNotification('error', 'Update Failed', 'Failed to update segmentation configuration');
        } finally {
            this.setLoading(false);
        }
    }

    async handleSubmitBalance() {
        try {
            this.setLoading(true);
            const { threshold, doubleDipCap, balance_rules, balance_enabled } = this.state;
            const aba = this.user?.fi?.aba || this.user?.access?.[0];

            if (!aba) {
                this.showNotification('error', 'Error', 'Unable to find FI ABA');
                return;
            }

            // Transform balance_rules to snake_case for API
            const transformedBalanceRules = this.transformBalanceRulesToSnakeCase(balance_rules, balance_enabled);

            const balancePayload = {
                threshold, // Already in decimal format
                balance_rules: transformedBalanceRules
            };

            if (doubleDipCap !== null && doubleDipCap !== undefined) {
                balancePayload.double_dip_cap = doubleDipCap;
            }

            const response = await this.data.updateFI(aba, {
                fi_info: balancePayload
            });

            if (response) {
                // Update original state (transform back to camelCase for comparison)
                const transformedBack = this.transformBalanceRulesToCamelCase(transformedBalanceRules);
                const { balanceEnabled, ...updatedBalanceRules } = transformedBack;
                this.setState((prevState) => ({
                    originalState: {
                        ...prevState.originalState,
                        threshold,
                        doubleDipCap,
                        balance_rules: JSON.parse(JSON.stringify(updatedBalanceRules)),
                        balance_enabled: JSON.parse(JSON.stringify(balanceEnabled))
                    }
                }));
                this.showNotification('success', 'Success', 'Available balance configuration updated successfully');
            }
        } catch (error) {
            console.error('Error updating balance:', error);
            this.showNotification('error', 'Update Failed', 'Failed to update available balance configuration');
        } finally {
            this.setLoading(false);
        }
    }

    async handleSubmitFee() {
        try {
            this.setLoading(true);
            const { fee, fee_policy, fee_rules, fee_enabled } = this.state;
            const aba = this.user?.fi?.aba || this.user?.access?.[0];

            if (!aba) {
                this.showNotification('error', 'Error', 'Unable to find FI ABA');
                return;
            }

            // Transform to snake_case for API
            const transformedFeePolicy = this.transformFeePolicyToSnakeCase(fee_policy);
            const transformedFeeRules = this.transformFeeRulesToSnakeCase(fee_rules);

            const response = await this.data.updateFI(aba, {
                fi_info: {
                    fee,
                    fee_policy: transformedFeePolicy,
                    fee_rules: transformedFeeRules,
                    fee_enabled
                }
            });

            if (response) {
                // Update original state (transform back to camelCase for comparison)
                const originalFeePolicy = this.transformFeePolicyToCamelCase(transformedFeePolicy);
                const originalFeeRules = this.transformFeeRulesToCamelCase(transformedFeeRules);
                this.setState((prevState) => ({
                    originalState: {
                        ...prevState.originalState,
                        fee,
                        fee_policy: JSON.parse(JSON.stringify(originalFeePolicy)),
                        fee_rules: JSON.parse(JSON.stringify(originalFeeRules)),
                        fee_enabled: JSON.parse(JSON.stringify(fee_enabled))
                    }
                }));
                this.showNotification('success', 'Success', 'Fee structure configuration updated successfully');
            }
        } catch (error) {
            console.error('Error updating fee:', error);
            this.showNotification('error', 'Update Failed', 'Failed to update fee structure configuration');
        } finally {
            this.setLoading(false);
        }
    }

    async handleSubmitLimits() {
        try {
            this.setLoading(true);
            const {
                limit_policy,
                limits_enabled,
                firstTimeSetup,
                segment_policy,
                threshold,
                doubleDipCap,
                balance_rules,
                balance_enabled,
                fee,
                fee_policy,
                fee_rules,
                fee_enabled
            } = this.state;
            const aba = this.user?.fi?.aba || this.user?.access?.[0];

            if (!aba) {
                this.showNotification('error', 'Error', 'Unable to find FI ABA');
                return;
            }

            if (firstTimeSetup) {
                // Submit all configuration data at once - transform all to snake_case
                const transformedSegmentPolicy = this.transformSegmentPolicyToSnakeCase(segment_policy);
                const transformedBalanceRules = this.transformBalanceRulesToSnakeCase(balance_rules, balance_enabled);
                const transformedFeePolicy = this.transformFeePolicyToSnakeCase(fee_policy);
                const transformedFeeRules = this.transformFeeRulesToSnakeCase(fee_rules);
                const transformedLimitPolicy = this.transformLimitPolicyToSnakeCase(limit_policy);

                // transformedSegmentPolicy already only contains fields with values (no empty strings)

                const response = await this.data.updateFI(aba, {
                    fi_info: {
                        segment_policy: transformedSegmentPolicy,
                        threshold,
                        ...(doubleDipCap !== null && doubleDipCap !== undefined ? { double_dip_cap: doubleDipCap } : {}),
                        balance_rules: transformedBalanceRules,
                        fee,
                        fee_policy: transformedFeePolicy,
                        fee_rules: transformedFeeRules,
                        fee_enabled,
                        limit_policy: transformedLimitPolicy,
                        limits_enabled
                    }
                });

                if (response) {
                    this.showNotification('success', 'Success', 'All configurations saved successfully');
                    // Navigate to success screen
                    this.goToNextStep();
                }
            } else {
                // Submit only limits - transform to snake_case
                const transformedLimitPolicy = this.transformLimitPolicyToSnakeCase(limit_policy);

                const response = await this.data.updateFI(aba, {
                    fi_info: { limit_policy: transformedLimitPolicy, limits_enabled }
                });

                if (response) {
                    // Update original state (transform back to camelCase for comparison)
                    const originalLimitPolicy = this.transformLimitPolicyToCamelCase(transformedLimitPolicy);
                    this.setState((prevState) => ({
                        originalState: {
                            ...prevState.originalState,
                            limit_policy: JSON.parse(JSON.stringify(originalLimitPolicy)),
                            limits_enabled: JSON.parse(JSON.stringify(limits_enabled))
                        }
                    }));
                    this.showNotification('success', 'Success', 'Limits configuration updated successfully');
                }
            }
        } catch (error) {
            console.error('Error updating limits:', error);
            this.showNotification('error', 'Update Failed', 'Failed to update limits configuration');
        } finally {
            this.setLoading(false);
        }
    }

    // Cancel handlers - revert to original state
    handleCancelSegmentation() {
        const { originalState } = this.state;
        if (originalState.segment_policy) {
            this.setSegmentPolicy(originalState.segment_policy);
        }
    }

    handleCancelBalance() {
        const { originalState } = this.state;
        if (originalState.threshold !== undefined && originalState.threshold !== null) {
            this.setThreshold(originalState.threshold * 100); // Convert to percentage
        }
        if (originalState.doubleDipCap !== undefined && originalState.doubleDipCap !== null) {
            this.setDoubleDipCap(originalState.doubleDipCap * 100);
        } else {
            this.setDoubleDipCap('');
        }
        if (originalState.balance_rules) {
            this.setBalanceRules(originalState.balance_rules, originalState.balance_enabled, this.state.segment_policy, this.state.segmentation_enabled);
        }
        if (originalState.balance_enabled) {
            this.setBalanceEnabled(originalState.balance_enabled);
        }
    }

    handleCancelFee() {
        const { originalState } = this.state;
        if (originalState.fee !== undefined && originalState.fee !== null) {
            this.setFee(originalState.fee);
        }
        if (originalState.fee_policy) {
            this.setFeePolicy(originalState.fee_policy);
        }
        if (originalState.fee_rules) {
            this.setFeeRules(originalState.fee_rules);
        }
        if (originalState.fee_enabled) {
            this.setFeeEnabled(originalState.fee_enabled);
        }
    }

    handleCancelLimits() {
        const { originalState } = this.state;
        if (originalState.limit_policy) {
            this.setLimitPolicy(originalState.limit_policy);
        }
        if (originalState.limits_enabled) {
            this.setLimitsEnabled(originalState.limits_enabled);
        }
    }

    // Notification method
    showNotification(type, message, description = '') {
        this.setState({
            notification: {
                show: true,
                type,
                message,
                description
            }
        });

        // Auto-hide after 4 seconds
        setTimeout(() => {
            this.setState((prevState) => ({
                notification: {
                    ...prevState.notification,
                    show: false
                }
            }));
        }, 4000);
    }

    // Segmentation methods
    getEligibilitySummary() {
        const { segment_policy, segmentation_enabled } = this.state;
        const { mode, whitelist, bankingTenure, accountTypes, avgDailyBalance, overdraftHistory, returnedACHs } = segment_policy || {};

        // Handle different modes
        if (mode === 'disabled') {
            return ['Service is currently disabled - no users have access'];
        }

        if (mode === 'open') {
            return ['All users have access to the service'];
        }

        if (mode === 'whitelist') {
            if (!whitelist || whitelist.length === 0) {
                return ['No members whitelisted yet'];
            }
            return [`${whitelist.length} member${whitelist.length === 1 ? '' : 's'} whitelisted for access`];
        }

        const summaries = {
            'more-than-30-days': 'Users that have been more than 30 days at your institution',
            'more-than-60-days': 'Users that have been more than 60 days at your institution',
            'more-than-90-days': 'Users that have been more than 90 days at your institution',
            'more-than-180-days': 'Users that have been more than 180 days at your institution',
            'more-than-365-days': 'Users that have been more than 365 days at your institution'
        };

        const accountTypeSummaries = {
            any: 'Users with any account type at your institution',
            'checking-only': 'Users with a checking accounts at your institution',
            'savings-only': 'Users with a savings accounts at your institution',
            'both-types': 'Users with both checking and savings accounts at your institution'
        };

        const balanceSummaries = {
            'less-than-500': 'Users with an Avg. daily Balance of less than $500',
            'less-than-1000': 'Users with an Avg. daily Balance of less than $1,000',
            'less-than-2500': 'Users with an Avg. daily Balance of less than $2,500',
            'less-than-5000': 'Users with an Avg. daily Balance of less than $5,000'
        };

        const overdraftSummaries = {
            'less-than-1': 'Users with less than 1 OD/NFS instances in the past 180 days',
            'less-than-2': 'Users with less than 2 OD/NFS instances in the past 180 days',
            'less-than-4': 'Users with less than 4 OD/NFS instances in the past 180 days',
            'less-than-6': 'Users with less than 6 OD/NFS instances in the past 180 days'
        };

        const achSummaries = {
            'less-than-1': 'Users with less than 1 ACH or Chargeback instances in the past 180 days',
            'less-than-2': 'Users with less than 2 ACH or Chargeback instances in the past 180 days',
            'less-than-3': 'Users with less than 3 ACH or Chargeback instances in the past 180 days',
            'less-than-5': 'Users with less than 5 ACH or Chargeback instances in the past 180 days'
        };

        const result = [];

        // Only include summaries for enabled fields
        if (segmentation_enabled.bankingTenure) {
            result.push(summaries[bankingTenure] || summaries['more-than-30-days']);
        }
        if (segmentation_enabled.accountTypes) {
            result.push(accountTypeSummaries[accountTypes] || accountTypeSummaries['all-types']);
        }
        if (segmentation_enabled.avgDailyBalance) {
            result.push(balanceSummaries[avgDailyBalance] || balanceSummaries['less-than-5000']);
        }
        if (segmentation_enabled.overdraftHistory) {
            result.push(overdraftSummaries[overdraftHistory] || overdraftSummaries['less-than-4']);
        }
        if (segmentation_enabled.returnedACHs) {
            result.push(achSummaries[returnedACHs] || achSummaries['less-than-3']);
        }

        return result;
    }

    // Available Balance methods
    getAvailableBalanceSummary() {
        const { threshold, doubleDipCap, balance_rules, balance_enabled } = this.state;
        const { userTenureRules, overdraftRules, achRules, employmentRules, balanceRules } = balance_rules || {};

        const grossPay = 1800.0;
        const payrollDeductions = 450.0;
        const netPay = grossPay - payrollDeductions;

        const sumDeductions = (rules = [], enabled = false) => {
            if (!enabled || !rules.length) return 0;
            return rules.reduce((total, rule) => total + (Number(rule.deduction) || 0), 0);
        };

        const userTenureDeduction = sumDeductions(userTenureRules, balance_enabled?.userTenure);
        const overdraftDeduction = sumDeductions(overdraftRules, balance_enabled?.overdraft);
        const achDeduction = sumDeductions(achRules, balance_enabled?.ach);
        const employmentDeduction = sumDeductions(employmentRules, balance_enabled?.employment);
        const balanceDeduction = sumDeductions(balanceRules, balance_enabled?.balance);

        const totalDeductions = userTenureDeduction + overdraftDeduction + achDeduction + employmentDeduction + balanceDeduction;
        // Convert deductions from percentage points to decimal (10% -> 0.10)
        const totalDeductionsDecimal = totalDeductions / 100;
        // Threshold is stored as decimal (0.75 = 75%), so subtract decimal deductions
        const finalThreshold = Math.max(0, threshold - totalDeductionsDecimal);
        // Calculate available balance using decimal threshold
        const availableBalance = netPay * finalThreshold;

        return {
            grossPay,
            payrollDeductions,
            netPay,
            threshold: threshold * 100, // Convert decimal to percentage for display (0.75 -> 75)
            doubleDipCap: (doubleDipCap ?? 0) * 100,
            userTenureDeduction,
            overdraftDeduction,
            achDeduction,
            employmentDeduction,
            balanceDeduction,
            finalThreshold: finalThreshold * 100, // Convert decimal to percentage for display (0.65 -> 65)
            availableBalance
        };
    }

    // Limits methods
    getLimitsSummary() {
        const { limit_policy } = this.state;
        const { maxAdvances, advanceTimeframe, minAdvanceAmount, maxAdvanceAmount, maxAdvanceVolume } = limit_policy || {};

        const timeframeLabels = {
            'pay-period': 'pay period',
            month: 'month',
            week: 'week',
            day: 'day'
        };

        return [
            `Maximum of ${maxAdvances} advances per ${timeframeLabels[advanceTimeframe]}`,
            `Minimum advance amount: $${minAdvanceAmount}`,
            `Maximum advance amount: $${maxAdvanceAmount}`,
            `Maximum advance volume per pay period: $${maxAdvanceVolume}`
        ];
    }
}

export default ConfigurationController;
